// import components
import AbsenceNotices from 'components/shared/AbsenceNotices/AbsenceNotices';
import { TabPanel } from 'components/shared';
import StyledTabs from 'components/shared/StyledTabs';
import TravelMovements from 'components/employee-card/employment/travel-movements/TravelMovements';
import AutoAskDutyRoundsOverview from './auto-ask-duty-rounds-overview/AutoAskDutyRoundsOverview';
import EmbeddedDashboard from './embedded-dashboard/EmbeddedDashboard';
import EndOfEmploymentSettlements from './end-of-employment-settlements/EndOfEmploymentSettlements';
import PlannerMessages from './messages/PlannerMessages';
import QualityMonitor from './quality-monitor/QualityMonitor';
import { TravelExpenseClaims } from './travel-expense-claims';
import LinearProgress from '@mui/material/LinearProgress';
// TODO: Activate the following when the BE is available
// import ExpiringEmploymentContracts from './expiring-employment-contracts/ExpiringEmploymentContracts';

// import enums
import { TabType } from './Home.enums';

// import external libraries
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RecoilRoot } from 'recoil';

// import hooks
import { useNavParams, useSelector } from 'hooks';
import useSubNavigation from 'hooks/useSubNavigation';
import DayLimitViolations from './day-limit-violations/DayLimitViolations';
import AdditionalCompensations from './additional-compensations/AdditionalCompensations';

const DojoMessagesContainer = () => {
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      window.dojoTopic.publish('renderRosterMessages', node);
    }
  }, []);

  useEffect(() => {
    return () => {
      window.dojoTopic.publish('destroyRosterMessages');
    };
  }, []);

  return <div ref={containerRef} style={{ position: 'absolute', inset: 0 }} />;
};

const RosterHome = () => {
  const { t } = useTranslation('common');
  const {
    hasRosterPortalMessagesAccess,
    hasEmbeddedDashboard,
    hasFeatureToggleExpenseClaims,
    hasFeatureToggleReactMessageBox,
    hasAbsenceNoticesViewAccess,
    hasDayLimitViolationsViewAccess,
    hasAdditionalCompensationsViewAccess,
    hasAutoAskDutyRoundsViewAccess,
    hasTravelMovementsFeature,
    hasProcessEndOfEmploymentSettlementAccess
  } = useSelector((state) => state.grants);
  const [isLoading, setIsLoading] = useState(false);
  const { params, removeParam } = useNavParams();

  const { activePage, setPage } = useSubNavigation<TabType>(
    TabType.QUALITY_MONITOR
  );

  const promoNodeRef = useRef(document.createElement('div'));

  useEffect(() => {
    setIsLoading(false);
  }, [activePage]);

  // remove date param when switching from travel expense claims tab
  useEffect(() => {
    if (params?.date && activePage !== TabType.TRAVEL_EXPENSE_CLAIMS) {
      removeParam('date');
    }
  }, [params]);

  return (
    <StyledTabs>
      <div className="progress-container">
        {isLoading && <LinearProgress />}
      </div>
      <div className="content">
        <TabPanel
          value={TabType.QUALITY_MONITOR}
          activePage={activePage}
          title={t('qualityMonitor')}
          style={{ minHeight: '100%', height: 'auto' }}
        >
          <QualityMonitor setPage={setPage} setIsLoading={setIsLoading} />
        </TabPanel>
        {hasRosterPortalMessagesAccess && hasFeatureToggleReactMessageBox && (
          <TabPanel
            value={TabType.MESSAGES}
            activePage={activePage}
            title={t('messages')}
            style={{ minHeight: '100%' }}
          >
            <PlannerMessages />
          </TabPanel>
        )}
        {hasRosterPortalMessagesAccess && !hasFeatureToggleReactMessageBox && (
          <TabPanel
            value={TabType.MESSAGES}
            activePage={activePage}
            title={t('messages')}
            style={{ height: '100%', position: 'relative' }}
          >
            <DojoMessagesContainer />
          </TabPanel>
        )}
        {hasEmbeddedDashboard && (
          <TabPanel
            title="Dashboard"
            value={TabType.DASHBOARD}
            activePage={activePage}
            style={{ height: 720, width: 1280, margin: 'auto' }}
          >
            <EmbeddedDashboard tabsPromoNode={promoNodeRef.current} />
          </TabPanel>
        )}
        {hasFeatureToggleExpenseClaims && (
          <TabPanel
            title={t('travelExpenseClaims.label')}
            value={TabType.TRAVEL_EXPENSE_CLAIMS}
            activePage={activePage}
          >
            <TravelExpenseClaims />
          </TabPanel>
        )}
        {hasTravelMovementsFeature && (
          <TabPanel
            title={t('travelMovements.label')}
            value={TabType.TRAVEL_MOVEMENTS}
            activePage={activePage}
          >
            <TravelMovements
              showActions
              isTravelMovementsBasedOnPlanUnit
              showEmployeeName
            />
          </TabPanel>
        )}
        {hasDayLimitViolationsViewAccess && (
          <TabPanel
            title={t('daylimitViolations')}
            value={TabType.DAY_LIMIT_VIOLATIONS}
            activePage={activePage}
          >
            <DayLimitViolations showEmployeeName />
          </TabPanel>
        )}
        {hasAdditionalCompensationsViewAccess && (
          <TabPanel
            title={t('additionalCompensations')}
            value={TabType.ADDITIONAL_COMPENSATIONS}
            activePage={activePage}
          >
            <AdditionalCompensations showEmployeeName />
          </TabPanel>
        )}
        {hasAbsenceNoticesViewAccess && (
          <TabPanel
            title={t('absenceNotices')}
            value={TabType.ABSENCE_NOTICES}
            activePage={activePage}
          >
            <RecoilRoot>
              <AbsenceNotices />
            </RecoilRoot>
          </TabPanel>
        )}
        {/* TODO: Activate this tab when the BE is available */}
        {/* {hasExpiringEmploymentContractsFeature && (
          <TabPanel
            title={t('expiringEmploymentContracts')}
            value={TabType.EXPIRING_EMPLOYMENT_CONTRACTS}
            activePage={activePage}
          >
            <RecoilRoot>
              <ExpiringEmploymentContracts />
            </RecoilRoot>
          </TabPanel>
        )} */}
        {hasProcessEndOfEmploymentSettlementAccess && (
          <TabPanel
            title={t('endOfEmploymentSettlements')}
            value={TabType.END_OF_EMPLOYMENT_SETTLEMENTS}
            activePage={activePage}
          >
            <RecoilRoot>
              <EndOfEmploymentSettlements />
            </RecoilRoot>
          </TabPanel>
        )}
        {hasAutoAskDutyRoundsViewAccess && (
          <TabPanel
            title={t('aiPlanning')}
            value={TabType.AI_MATCH}
            activePage={activePage}
          >
            <RecoilRoot>
              <AutoAskDutyRoundsOverview />
            </RecoilRoot>
          </TabPanel>
        )}
      </div>
    </StyledTabs>
  );
};

export default RosterHome;
